import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import isEqual from 'lodash/isEqual'

import { PageVariablesContext } from '@components/Variables'
import { useHandlers } from '@components/Variables/hooks/useHandlers'
import { VariableValue } from '@components/Variables/types'

import {
  useCreateVariableMutation,
  useDeleteVariableMutation,
  useUpdateVariableMutation,
} from '@redux/api/variables.api'

import { POSTVariableDTO } from '@types'

type useVariableProps = {
  index?: number
  variables?: VariableValue[]
  removeVariable?: (index: number) => void
  initialVariables?: VariableValue[]
}

export const useVariable = ({
  index,
  variables,
  removeVariable,
  initialVariables,
}: useVariableProps) => {
  const [isSavingVariableValues, setIsSavingVariableValues] = useState(false)

  const { t } = useTranslation()

  const { data, handlers } = useHandlers({ index, variables, removeVariable, initialVariables })

  const { currentVariables, isCreateOrEditVariable, entityId, entityType } =
    useContext(PageVariablesContext)

  const { handleSubmit, handleEditVariable } = handlers
  const { activeVariable, isCreateOrEdit } = data

  const [createVariable] = useCreateVariableMutation()
  const [updateVariable] = useUpdateVariableMutation()
  const [deleteVariable] = useDeleteVariableMutation()

  const validateTitleAndIdVariable = (value: string) => {
    const copyArrayCurrentVariables = currentVariables ? [...currentVariables] : []

    const activeVariableIndex = copyArrayCurrentVariables?.findIndex(variable => {
      return variable.id === activeVariable.id
    })

    if (activeVariableIndex !== -1) {
      copyArrayCurrentVariables.splice(activeVariableIndex, 1)
    }

    if (
      copyArrayCurrentVariables &&
      copyArrayCurrentVariables.some(variable => variable.title === value)
    ) {
      return t('configEntity.variables.error.alreadyUsed', { title: value })
    }

    if (value.trim().length !== 0) {
      return true
    }
  }

  const handleSave = handleSubmit(async () => {
    setIsSavingVariableValues(true)

    const data = {
      id: activeVariable?.id ?? null,
      internalId: activeVariable?.internalId,
      title: activeVariable?.title,
      variableType: activeVariable?.variableType?.id,
      valueType: activeVariable?.valueType?.id,
      entityType: entityType || '',
      entityId,
      defaultValue: '',
    } as unknown as POSTVariableDTO

    const transformExistingVariable =
      currentVariables && index && currentVariables[index]
        ? Object.assign(
            {},
            (({ title, valueType, variableType }) => ({ title, valueType, variableType }))(
              currentVariables[index]
            )
          )
        : null

    const transformCurrentVariable = {
      title: activeVariable?.title,
      variableType: activeVariable?.variableType?.id,
      valueType: activeVariable?.valueType?.id,
    }

    const isEqualObjects = isEqual(transformCurrentVariable, transformExistingVariable)

    if (isCreateOrEdit && !isEqualObjects) {
      updateVariable(data)
        .unwrap()
        .then(() => {
          handleEditVariable(false)
        })
        .finally(() => {
          setIsSavingVariableValues(false)
        })

      return
    }

    if (!isCreateOrEdit) {
      createVariable(data)
        .unwrap()
        .then(() => {
          handleEditVariable(false)
        })
        .finally(() => {
          setIsSavingVariableValues(false)
        })
    }
  })

  const handleDelete = () => {
    if (activeVariable?.id) {
      removeVariable?.(index)
      deleteVariable(activeVariable?.id)
    }
  }

  return {
    handlers: { ...handlers, handleSave, handleDelete, validateTitleAndIdVariable },
    data: {
      ...data,
      isCreateOrEditVariable,
      isSavingVariableValues,
      currentVariables,
    },
  }
}
